/**
 * CustomersPage — VEN-01 Mantenedor de Clientes
 */
import { ordenControlado } from '@/components/ds/ordenControlado'
import { sortStateToParam, type SortState } from '@/components/SortableHeader'
import { useState, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Plus, PencilLine, UserX, UserCheck, Eye,
  Phone, Mail, Building2, Loader2, Users, Upload
} from 'lucide-react'
import { FilterBar, useFilters, type FilterConfig } from '@/components/filters'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import { DataTable, type DataTableColumn } from '@/components/ds/DataTable'
import { KebabMenu } from '@/components/KebabMenu'
import { ConfirmModal } from '@/components/ConfirmModal'
import { extractFieldErrors } from '@/lib/errorUtils'
import {
  useCustomers, useCustomer, useCreateCustomer, useUpdateCustomer,
  useDeactivateCustomer, useRestoreCustomer, useDriverInfo,
  useImportCustomersCSV,
  type CustomerListItem,
} from '@/modules/sales/hooks/useSales'
import { formatRut } from '@/lib/utils'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { CustomerModal } from './CustomerModal'
import { ImportCSVMapperModal, type SystemField } from '@/components/ImportCSVMapperModal'
import { ToastContainer, useToast } from '@/components/Toast'
import { usePermission } from '@/hooks/usePermission'
import { Pagination } from '@/components/ds/Pagination'
import { useTenantLocale } from '@/lib/locale'

const CUSTOMER_FIELDS: SystemField[] = [
  { key: 'legal_name', label: 'Razón Social', required: true, aliases: ['razon_social', 'legal_name', 'nombre'] },
  { key: 'trade_name', label: 'Nombre Fantasía', aliases: ['nombre_fantasia', 'trade_name'] },
  { key: 'tax_id', label: 'RUT / ID Tributario', required: true, aliases: ['rut', 'tax_id', 'ruc', 'cuit', 'rfc'] },
  { key: 'person_type', label: 'Tipo Persona', aliases: ['tipo_persona', 'person_type'] },
  { key: 'email_contact', label: 'Email Contacto', aliases: ['email', 'email_contact', 'email_contacto'] },
  { key: 'email_dte', label: 'Email DTE', aliases: ['email_dte'] },
  { key: 'phone', label: 'Teléfono', aliases: ['telefono', 'phone'] },
  { key: 'contact_name', label: 'Nombre Contacto', aliases: ['contacto', 'contact_name', 'nombre_contacto'] },
  { key: 'giro', label: 'Giro', aliases: ['giro'] },
  { key: 'address_street', label: 'Dirección', aliases: ['direccion', 'address_street'] },
  { key: 'address_commune', label: 'Comuna', aliases: ['comuna', 'address_commune'] },
  { key: 'address_city', label: 'Ciudad', aliases: ['ciudad', 'address_city'] },
  { key: 'address_region', label: 'Región', aliases: ['region', 'address_region'] },
  { key: 'address_country', label: 'País', aliases: ['pais', 'address_country'] },
  { key: 'credit_limit', label: 'Límite de Crédito', aliases: ['limite_credito', 'credit_limit'] },
  { key: 'payment_term_days', label: 'Plazo de Pago', aliases: ['plazo_pago', 'payment_term_days'] },
  { key: 'notes', label: 'Notas', aliases: ['notas', 'notes'] },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
      ${active ? 'bg-success-surface text-success-content' : 'bg-surface-muted text-content-muted'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-success-solid' : 'bg-content-subtle'}`} />
      {active ? 'Activo' : 'Inactivo'}
    </span>
  )
}

// ─── Row con menú de acciones ─────────────────────────────────────────────────

/**
 * Las columnas del listado de clientes.
 *
 * 🔴 Esto era `CustomerRow`, un componente que emitía su propio `<tr>` — el
 * bloqueo «componente-fila» del inventario. Era render puro, así que se desarma
 * en columnas sin perder nada y la tabla gana filas alternas, orden por clic y
 * la densidad del sistema.
 */
function columnasDeCliente({
  taxIdLabel, canEdit, onEdit, onView, onDeactivate, onRestore,
}: {
  taxIdLabel: string
  canEdit: boolean
  onEdit: (c: CustomerListItem) => void
  onView: (c: CustomerListItem) => void
  onDeactivate: (c: CustomerListItem) => void
  onRestore: (c: CustomerListItem) => void
}): DataTableColumn<CustomerListItem>[] {
  return [
    { key: 'legal_name', header: 'Cliente', sortable: true,
      render: c => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
            <Building2 size={14} className="text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium text-content">{c.legal_name}</p>
            {c.trade_name && <p className="text-xs text-content-subtle">{c.trade_name}</p>}
          </div>
        </div>
      ) },
    { key: 'tax_id', header: taxIdLabel, sortable: true,
      render: c => <span className="text-content-muted">{formatRut(c.tax_id)}</span> },
    { key: 'contacto', header: 'Contacto',
      render: c => (
        <div className="space-y-0.5">
          {c.email_contact && (
            <p className="flex items-center gap-1 text-xs text-content-muted">
              <Mail size={11} />{c.email_contact}
            </p>
          )}
          {c.phone && (
            <p className="flex items-center gap-1 text-xs text-content-muted">
              <Phone size={11} />{c.phone}
            </p>
          )}
        </div>
      ) },
    { key: 'address_city', header: 'Ciudad', sortable: true,
      render: c => <span className="text-content-muted">{c.address_city || '—'}</span> },
    { key: 'is_active', header: 'Estado', sortable: true,
      sortValue: c => (c.is_active ? 1 : 0),
      render: c => <StatusBadge active={c.is_active} /> },
    { key: 'acciones', header: '', width: 180,
      render: c => (
        <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
          {canEdit ? (
            <>
              <button
                onClick={() => onEdit(c)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors"
                title="Editar"
              >
                <PencilLine size={11} />Editar
              </button>
              <KebabMenu items={[
                { label: c.is_active ? 'Desactivar' : 'Reactivar', icon: c.is_active ? <UserX size={14} /> : <UserCheck size={14} />, onClick: () => (c.is_active ? onDeactivate(c) : onRestore(c)), className: c.is_active ? 'text-danger-content-soft hover:bg-danger-surface' : 'text-success-content-soft hover:bg-success-surface' },
              ]} />
            </>
          ) : (
            <button
              onClick={() => onView(c)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors"
              title="Ver detalles"
            >
              <Eye size={11} />Ver
            </button>
          )}
        </div>
      ) },
  ]
}

// ─── CustomersPage ────────────────────────────────────────────────────────────

export function CustomersPage() {
  const locale = useTenantLocale()
  const [page, setPage] = useState(1)
  // El orden va al SERVIDOR (`?ordering=`): esta lista la pagina el backend,
  // y ordenar en el navegador ordenaría solo la página visible.
  const [orden, setOrden] = useState<SortState | null>(null)
  const { can } = usePermission()
  const canCreate = can('sales.customers', 'create')
  const canEdit = can('sales.customers', 'edit')

  const filterConfig: FilterConfig = useMemo(() => [
    { key: 'search', type: 'search' as const, placement: 'quick' as const,
      placeholder: 'Nombre, RUT, email...' },
    { key: 'active', type: 'segment' as const, placement: 'quick' as const,
      options: [
        { value: 'true', label: 'Activos' },
        { value: 'all', label: 'Todos' },
      ] },
  ], [])

  const filters = useFilters(filterConfig, { onFilterChange: () => setPage(1) })
  const [modalOpen, setModalOpen] = useState(false)
  const [modalReadOnly, setModalReadOnly] = useState(false)
  const [editing, setEditing] = useState<Partial<CustomerListItem> | null>(null)
  const toast = useToast()
  const [confirming, setConfirming] = useState<{ customer: CustomerListItem; action: 'deactivate' | 'restore' } | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const [serverErrors, setServerErrors] = useState<Record<string, string>>()

  const { data, isLoading } = useCustomers({ ordering: sortStateToParam(orden),
    search: filters.params.search,
    active: (filters.params.active as 'true' | 'all') || 'true',
    page,
    page_size: 20,
  })
  const { data: driverInfo } = useDriverInfo()
  const { mutate: create, isPending: isCreating } = useCreateCustomer()
  const { mutate: deactivate, isPending: isDeactivating } = useDeactivateCustomer()
  const { mutate: restore, isPending: isRestoring } = useRestoreCustomer()
  const importMutation = useImportCustomersCSV()

  const taxIdLabel = driverInfo?.tax_id_label || 'RUT'

  // Deep-link ?open=<id> (p. ej. desde "Ver ficha del cliente" en la Vista 360):
  // abre la ficha del cliente (fetch-by-id) y limpia la URL.
  const [searchParams, setSearchParams] = useSearchParams()
  const openId = searchParams.get('open')
  const { data: openCustomer } = useCustomer(openId ?? '')
  useEffect(() => {
    if (openId && openCustomer) {
      setEditing(openCustomer as Partial<CustomerListItem>)
      setModalReadOnly(true)
      setServerErrors(undefined)
      setModalOpen(true)
      const next = new URLSearchParams(searchParams)
      next.delete('open')
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, openCustomer])

  const handleEdit = (customer: CustomerListItem) => {
    setEditing(customer)
    setModalReadOnly(false)
    setServerErrors(undefined)
    setModalOpen(true)
  }

  const handleView = (customer: CustomerListItem) => {
    setEditing(customer)
    setModalReadOnly(true)
    setServerErrors(undefined)
    setModalOpen(true)
  }

  const handleCreate = () => {
    setEditing(null)
    setModalReadOnly(false)
    setServerErrors(undefined)
    setModalOpen(true)
  }

  // Carga el detalle completo del cliente al editar (incluye payment_term_days, credit_limit, notes)
  const { data: editingDetail } = useCustomer(editing?.id || '')
  const updateMutation = useUpdateCustomer(editing?.id || '')

  const handleModalSubmit = (data: any) => {
    setServerErrors(undefined)
    if (editing?.id) {
      updateMutation.mutate(data, {
        onSuccess: () => { setModalOpen(false); toast.success('Cliente actualizado correctamente.') },
        onError: (e) => {
          const fields = extractFieldErrors(e)
          if (fields) { setServerErrors(fields) } else { toast.error('Error al actualizar el cliente.') }
        },
      })
    } else {
      create(data, {
        onSuccess: () => { setModalOpen(false); toast.success('Cliente creado correctamente.') },
        onError: (e) => {
          const fields = extractFieldErrors(e)
          if (fields) { setServerErrors(fields) } else { toast.error('Error al crear el cliente.') }
        },
      })
    }
  }

  const handleConfirmAction = () => {
    if (!confirming) return
    if (confirming.action === 'deactivate') {
      deactivate(confirming.customer.id, { onSuccess: () => setConfirming(null) })
    } else {
      restore(confirming.customer.id, { onSuccess: () => setConfirming(null) })
    }
  }

  const customers = data?.customers || []
  const total = data?.count || 0
  const pages = data?.pages || 1

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <PageHeader
        title="Clientes"
        subtitle={`${total} ${total === 1 ? 'cliente' : 'clientes'} registrados`}
        help={HELP['sales.customers']}
      >
        {canCreate && (
          <button onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-content border border-divider rounded-lg hover:bg-soft transition-colors">
            <Upload size={16} />Importar CSV
          </button>
        )}
        {canCreate && (
          <button onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors">
            <Plus size={16} />Nuevo cliente
          </button>
        )}
      </PageHeader>

      {/* Filtros */}
      <FilterBar config={filterConfig} filters={filters} />

      {/* Tabla */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
        {isLoading ? (
          <TableSkeleton rows={8} cols={6} />
        ) : customers.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No hay clientes"
            hint={filters.params.search ? 'Prueba con otro término de búsqueda.' : 'Crea el primer cliente con el botón de arriba.'}
          />
        ) : (
          <DataTable<CustomerListItem>
            {...ordenControlado(orden, s => { setOrden(s); setPage(1) })}
            columns={columnasDeCliente({
              taxIdLabel, canEdit,
              onEdit: handleEdit,
              onView: handleView,
              onDeactivate: c => setConfirming({ customer: c, action: 'deactivate' }),
              onRestore: c => setConfirming({ customer: c, action: 'restore' }),
            })}
            rows={customers}
            rowKey={c => c.id}
            minWidth={900}
            surface={false}
            onRowClick={c => (canEdit ? handleEdit(c) : handleView(c))}
            caption="Clientes registrados"
          />
        )}
      </div>

      {/* Paginación */}
      {pages > 1 && (
        <Pagination
          page={page}
          pageSize={20}
          total={total}
          onPageChange={setPage}
          locale={locale}
          className="mt-4"
        />
      )}

      {/* Modals */}
      <CustomerModal
        key={editing?.id ?? 'new'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleModalSubmit}
        isPending={editing?.id ? updateMutation.isPending : isCreating}
        initial={editing?.id ? (editingDetail ?? null) : null}
        taxIdLabel={taxIdLabel}
        serverErrors={serverErrors}
        readOnly={modalReadOnly}
      />

      <ConfirmModal
        open={confirming !== null}
        message={
          confirming?.action === 'deactivate'
            ? `¿Desactivar a "${confirming.customer.legal_name}"? No podrá seleccionarlo para nuevos documentos de venta.`
            : confirming
              ? `¿Reactivar a "${confirming.customer.legal_name}"?`
              : ''
        }
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirming(null)}
        isPending={isDeactivating || isRestoring}
        variant={confirming?.action === 'deactivate' ? 'danger' : 'success'}
      />

      <ImportCSVMapperModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Importar Clientes"
        description="Suba un archivo CSV con los datos de sus clientes. Si un cliente ya existe (mismo RUT/ID Tributario), se actualizará."
        systemFields={CUSTOMER_FIELDS}
        templateFileName="plantilla_clientes.csv"
        onImport={(file, mapping) => importMutation.mutateAsync({ file, mapping })}
        isPending={importMutation.isPending}
      />

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}
